import createClient, { Middleware } from 'openapi-fetch';

import { buildApiUrl } from '@/config/runtimeConfig';

import type { paths } from './openapi';
import { ValidationApiError } from './ValidationApiError';

/**
 * Type for the token getter function
 */
export type TokenGetter = () => Promise<string | undefined>;

/**
 * Options for configuring the API client
 */
export interface ApiClientOptions {
  /**
   * Cache option for fetch requests.
   * Defaults to 'no-store' if not specified.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Request/cache
   */
  cache?: RequestCache;
  /**
   * Abort signal for cancelling requests.
   */
  signal?: AbortSignal;
}

/**
 * Creates error handling middleware for API responses.
 */
const errorMiddleware: Middleware = {
  async onResponse({ response }) {
    if (!response.ok) {
      const errorData = await response
        .clone()
        .json()
        .catch(() => ({}));

      if (errorData.errors && typeof errorData.errors === 'object') {
        throw new ValidationApiError(
          `HTTP ${response.status}: ${errorData.title || response.statusText}`,
          errorData.errors as Record<string, string[]>,
        );
      }

      throw new Error(`HTTP ${response.status}: ${errorData.detail || response.statusText} - Trace Id: ${errorData.traceId || 'N/A'}`);
    }
    return response;
  },
};

/**
 * Creates authentication middleware that injects the Authorization header.
 *
 * @param getToken - Function that returns the access token
 * @returns Middleware instance
 */
function createAuthMiddleware(getToken: TokenGetter): Middleware {
  return {
    async onRequest({ request }) {
      const token = await getToken();

      if (token) {
        request.headers.set('Authorization', `Bearer ${token}`);
      }

      return request;
    },
  };
}

/**
 * Creates an OpenAPI fetch client with automatic authentication.
 * The client automatically injects the Authorization header using the provided token getter.
 *
 * @param getToken - Function that returns the access token (typically from useAuth hook)
 * @param options - Optional configuration for the API client
 * @returns Configured OpenAPI fetch client
 */
export function createApiClient(getToken: TokenGetter, options?: ApiClientOptions) {
  const baseUrl = buildApiUrl('');
  const cacheOption = options?.cache ?? 'no-store';
  const signalOption = options?.signal;

  const client = createClient<paths>({
    baseUrl,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    cache: cacheOption,
    signal: signalOption,
  });

  // Register middlewares in order: auth first, then error handling
  client.use(createAuthMiddleware(getToken));
  client.use(errorMiddleware);

  return client;
}
